'use client'

import { Button, Flex } from '@chakra-ui/react'
import type { IRDocument } from '@/utils/investorRelations'

interface DocumentYearFilterProps {
  years: number[]
  selectedYear: number | null
  onYearChange: (year: number) => void
  primaryColor: string
  fontFamily?: string
  sectionTitle: string
}

export function getPublishedYear(
  publishedDate: string | null | undefined
): number | null {
  if (!publishedDate || publishedDate === '[Not published]') return null

  const date = new Date(publishedDate)
  if (Number.isNaN(date.getTime())) return null

  return date.getFullYear()
}

export function getAvailableDocumentYears(
  documents: readonly IRDocument[]
): number[] {
  const years = new Set<number>()

  for (const document of documents) {
    const year = getPublishedYear(document.publishedDate)
    if (year !== null) years.add(year)
  }

  return Array.from(years).sort((a, b) => b - a)
}

export function DocumentYearFilter({
  years,
  selectedYear,
  onYearChange,
  primaryColor,
  fontFamily,
  sectionTitle,
}: DocumentYearFilterProps) {
  if (years.length < 2) return null

  return (
    <Flex
      role="group"
      aria-label={`Filter ${sectionTitle} by year`}
      justify="center"
      gap={3}
      flexWrap="wrap"
    >
      {years.map((year) => {
        const isSelected = year === selectedYear

        return (
          <Button
            key={year}
            type="button"
            aria-pressed={isSelected}
            onClick={() => onYearChange(year)}
            minW="80px"
            bg={isSelected ? primaryColor : 'white'}
            color={isSelected ? 'white' : primaryColor}
            border="1px solid"
            borderColor={primaryColor}
            borderRadius="full"
            fontFamily={fontFamily}
            fontWeight="semibold"
            _hover={{
              bg: isSelected ? primaryColor : `${primaryColor}10`,
            }}
            _focusVisible={{
              outline: '2px solid',
              outlineColor: primaryColor,
              outlineOffset: '2px',
            }}
          >
            {year}
          </Button>
        )
      })}
    </Flex>
  )
}
